import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://dev.mithahomes.com/backend";

const SepartedpageTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("TEXT");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null); // New state for image modal
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/testimonials/all`);
        const data = await response.json();
        if (data.success) {
          // Filter to ensure we have at least a title and role to display
          const filteredData = data.data.filter((t) => t.ttitle);
          setTestimonials(filteredData);
        } else {
          setError("Failed to fetch testimonials");
        }
      } catch (err) {
        setError("Error connecting to API");
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  // --- Styles to Match Image Exactly ---
  const containerStyle = {
    padding: "40px 20px",
    backgroundColor: "#fff",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  };

  const tabWrapperStyle = {
    display: "flex",
    justifyContent: "center",
    marginBottom: "50px",
  };

  const toggleContainer = {
    display: "flex",
    backgroundColor: "#eeeeee",
    borderRadius: "30px",
    width: "280px",
    height: "45px",
    padding: "0",
    overflow: "hidden",
  };

  const tabButtonStyle = (isActive) => ({
    flex: 1,
    border: "none",
    borderRadius: "30px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    transition: "all 0.3s ease",
    backgroundColor: isActive ? "rgb(94 186 75)" : "transparent",
    color: isActive ? "#fff" : "#888",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textTransform: "uppercase",
  });

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "25px",
    maxWidth: "1200px",
    margin: "0 auto",
  };

  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "10px",
    padding: "25px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #f0f0f0",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "translateY(-5px)",
    },
  };

  const profileHeader = {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
  };

  const avatarStyle = {
    width: "55px",
    height: "55px",
    borderRadius: "50%",
    marginRight: "15px",
    objectFit: "cover",
    border: "1px solid #eee",
    backgroundColor: "#f9f9f9",
  };

  const nameStyle = (isDoctor) => ({
    fontSize: "17px",
    fontWeight: "700",
    color: isDoctor ? "#f39200" : "#333",
    margin: "0 0 3px 0",
    lineHeight: "1.2",
  });

  const subStyle = {
    fontSize: "13px",
    color: "#666",
    margin: 0,
    lineHeight: "1.4",
    fontWeight: "500",
  };

  const textStyle = {
    fontSize: "14px",
    color: "#555",
    lineHeight: "1.6",
    marginBottom: "15px",
    flexGrow: 1,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: "4",
    WebkitBoxOrient: "vertical",
  };

  const readMoreStyle = {
    color: "#f39200",
    fontWeight: "700",
    textDecoration: "none",
    fontSize: "13px",
    cursor: "pointer",
    marginTop: "auto",
    "&:hover": {
      textDecoration: "underline",
    },
  };

  // Function to check if file is an image (not video)
  const isImageFile = (url) => {
    if (!url) return false;

    const extension = url.split(".").pop().toLowerCase();
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

    return imageExtensions.includes(extension);
  };

  // Function to get avatar URL
  const getAvatarUrl = (item) => {
    if (item.timg && isImageFile(item.timg)) {
      // If timg is an image file, use it as avatar
      return `${BASE_URL}${item.timg}`;
    }

    // Fallback to initials avatar
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(item.ttitle || "User")}&background=random`;
  };

  // Function to get main image URL for TEXT tab
  const getMainImageUrl = (item) => {
    if (item.timg && isImageFile(item.timg)) {
      return `${BASE_URL}${item.timg}`;
    }
    return null;
  };

  // --- Render Logic ---
  const renderCard = (item) => {
    const title = item.ttitle || "";
    const isDoctor = title.toLowerCase().includes("dr.");
    const isVideo = activeTab === "VIDEO";

    const avatarUrl = getAvatarUrl(item);
    const mainImageUrl = getMainImageUrl(item);

    return (
      <div key={item.tid} style={cardStyle}>
        <div style={profileHeader}>
          <img
            src={avatarUrl}
            alt={title}
            style={avatarStyle}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(title)}&background=random`;
            }}
          />
          <div>
            <h3 style={nameStyle(isDoctor)}>{title}</h3>
            <p style={subStyle}>{item.tsub || "Customer"}</p>
          </div>
        </div>

        {isVideo ? (
          // VIDEO TAB: Show video thumbnail with play button
          <div
            onClick={() => setSelectedVideo(item)}
            style={{
              backgroundColor: "#000",
              height: "160px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
              marginBottom: "15px",
            }}
          >
            {/* You might want to add thumbnails to your API for videos */}
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "#333",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  backgroundColor: "#f39200",
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  color: "#fff",
                }}
              >
                ▶
              </div>
            </div>
          </div>
        ) : (
          // TEXT TAB: Show full image if available
          <>
            <p style={textStyle}>{item.tdet || "No details provided."}</p>

            {item.tdet && item.tdet.length > 200 && (
              <div
                style={readMoreStyle}
                onClick={() =>
                  navigate(`/testimonials/${item.tid}`, {
                    state: { testimonial: item },
                  })
                }
              >
                Read more
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "100px", color: "#888" }}>
        Loading Testimonials...
      </div>
    );
  if (error)
    return (
      <div style={{ textAlign: "center", padding: "100px", color: "red" }}>
        {error}
      </div>
    );

  return (
    <div style={containerStyle}>
      {/* Design Matched Toggle */}
      <div style={tabWrapperStyle}>
        <div style={toggleContainer}>
          <button
            style={tabButtonStyle(activeTab === "TEXT")}
            onClick={() => setActiveTab("TEXT")}
          >
            TEXT
          </button>
          <button
            style={tabButtonStyle(activeTab === "VIDEO")}
            onClick={() => setActiveTab("VIDEO")}
          >
            VIDEO
          </button>
        </div>
      </div>

      {/* Responsive Grid */}
      <div style={gridStyle}>
        {testimonials
          .filter((t) =>
            activeTab === "VIDEO" ? t.ttype === "video" : t.ttype !== "video",
          )
          .map(renderCard)}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.9)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClick={() => setSelectedVideo(null)}
        >
          <div
            style={{
              position: "relative",
              width: "80%",
              maxWidth: "800px",
              backgroundColor: "#000",
              borderRadius: "10px",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <video controls autoPlay style={{ width: "100%" }}>
              <source
                src={`${BASE_URL}${selectedVideo.timg}`}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
            <div
              style={{
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <button
                onClick={() => setSelectedVideo(null)}
                style={{
                  background: "rgba(0,0,0,0.7)",
                  border: "none",
                  color: "#fff",
                  fontSize: "20px",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Video info */}
            <div
              style={{
                padding: "15px",
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "#fff",
              }}
            >
              <h3 style={{ margin: "0 0 5px 0", color: "#f39200" }}>
                {selectedVideo.ttitle}
              </h3>
              <p style={{ margin: 0, fontSize: "14px" }}>
                {selectedVideo.tsub}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.9)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClick={() => setSelectedImage(null)}
        >
          <div
            style={{
              position: "relative",
              width: "80%",
              maxWidth: "800px",
              backgroundColor: "#000",
              borderRadius: "10px",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getMainImageUrl(selectedImage)}
              alt={selectedImage.ttitle}
              style={{
                width: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <button
                onClick={() => setSelectedImage(null)}
                style={{
                  background: "rgba(0,0,0,0.7)",
                  border: "none",
                  color: "#fff",
                  fontSize: "20px",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Image info */}
            <div
              style={{
                padding: "15px",
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "#fff",
              }}
            >
              <h3 style={{ margin: "0 0 5px 0", color: "#f39200" }}>
                {selectedImage.ttitle}
              </h3>
              <p style={{ margin: 0, fontSize: "14px" }}>
                {selectedImage.tsub}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SepartedpageTestimonials;
